import { date } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  EmptyState,
  Prompt,
  SectionHeader,
  StatusBadge,
  Table,
  toast,
} from "@lumberset/ui";
import { ArrowPath, Sparkles } from "@medusajs/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Crumbs } from "../../components/crumbs";
import {
  type AdminOffer,
  type OfferStatusFilter,
  offerActions,
  offerStatuses,
  offerStatusLooks,
  offersQuery,
  useRefreshOffers,
} from "../../lib/queries-offers";

const filters: { value: OfferStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  ...offerStatuses.map((status) => ({ value: status, label: offerStatusLooks[status].label })),
];

export const Route = createFileRoute("/_app/offers")({
  validateSearch: (search: Record<string, unknown>): { status?: OfferStatusFilter } =>
    filters.some((filter) => filter.value === search.status && filter.value !== "all")
      ? { status: search.status as OfferStatusFilter }
      : {},
  component: Offers,
});

const plural = (value: number, one: string, many = `${one}s`) =>
  `${value} ${value === 1 ? one : many}`;

/**
 * Every custom plan offer: who it went to, its terms, and what the workspace
 * did with it. Offers are sent from a workspace's page, under Billing.
 */
function Offers() {
  const navigate = useNavigate();
  const status = Route.useSearch().status ?? "all";
  const { data, isPending } = useQuery(offersQuery({ status }));
  const [withdrawing, setWithdrawing] = useState<AdminOffer | null>(null);
  const [renewing, setRenewing] = useState(false);
  const refresh = useRefreshOffers();

  const withdraw = useMutation({
    mutationFn: (offer: AdminOffer) => offerActions.withdraw(offer.id),
    onSuccess: async ({ offer }) => {
      setWithdrawing(null);
      await refresh();
      toast.success("Offer withdrawn", { description: offer.workspaceName });
    },
    onError: (error) => toast.error(error.message),
  });

  const renew = useMutation({
    mutationFn: offerActions.runRenewals,
    onSuccess: async ({ results, expiredOffers }) => {
      setRenewing(false);
      await refresh();
      const billed = results.filter((result) => result.action === "billed").length;
      const left = results.filter((result) => result.action === "left").length;
      const failed = results.filter((result) => result.action === "error").length;
      toast.success(billed > 0 ? `${plural(billed, "monthly fee")} charged` : "Nothing was due", {
        description: [
          `${plural(results.length, "workspace")} on a monthly plan checked.`,
          left > 0 ? `${plural(left, "workspace")} moved off a canceled plan.` : null,
          expiredOffers > 0 ? `${plural(expiredOffers, "offer")} expired.` : null,
          failed > 0 ? `${failed} could not be renewed; the API log has why.` : null,
        ]
          .filter(Boolean)
          .join(" "),
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const offers = data?.offers ?? [];

  return (
    <>
      <Crumbs trail={[{ label: "Custom plan offers" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Custom plan offers"
          description="Monthly plans offered to one workspace, usually after a call. Send one from the workspace's page, under Billing; the owners accept or decline it in their Billing."
          actions={
            <Button size="small" variant="secondary" onClick={() => setRenewing(true)}>
              <ArrowPath /> Renew now
            </Button>
          }
        />
        <nav
          className="scrollbar-none flex items-center gap-x-1 overflow-x-auto px-6 py-3"
          aria-label="Filter offers"
        >
          {filters.map((filter) => (
            <Button
              key={filter.value}
              size="small"
              variant={status === filter.value ? "secondary" : "transparent"}
              aria-current={status === filter.value ? "page" : undefined}
              className="shrink-0"
              onClick={() =>
                navigate({
                  to: "/offers",
                  search: filter.value === "all" ? {} : { status: filter.value },
                })
              }
            >
              {filter.label}
            </Button>
          ))}
        </nav>

        {isPending ? null : offers.length === 0 ? (
          <EmptyState
            icon={<Sparkles />}
            title={status === "all" ? "No offers yet" : "No offers here"}
            description="Open a workspace, then Billing, then Send a custom plan offer. Requests for one arrive as call request tickets titled Custom plan request."
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Workspace</Table.HeaderCell>
                <Table.HeaderCell>Plan</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Sent</Table.HeaderCell>
                <Table.HeaderCell>Answered</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {offers.map((offer) => {
                const look = offerStatusLooks[offer.status];
                return (
                  <Table.Row key={offer.id}>
                    <Table.Cell className="whitespace-nowrap">
                      <Link
                        to="/yards/$id"
                        params={{ id: offer.yardId }}
                        className="txt-compact-small-plus text-ui-fg-base hover:text-ui-fg-interactive"
                      >
                        {offer.workspaceName}
                      </Link>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="min-w-56 text-ui-fg-base">{offer.plan.name}</div>
                      <div className="txt-compact-xsmall min-w-56 text-pretty text-ui-fg-muted">
                        {offer.terms}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      <StatusBadge color={look.tone}>{look.label}</StatusBadge>
                      {offer.status === "pending" && offer.expiresAt ? (
                        <div className="txt-compact-xsmall text-ui-fg-muted">
                          Until {date(offer.expiresAt)}
                        </div>
                      ) : null}
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      <div>{date(offer.sentAt ?? offer.createdAt)}</div>
                      <div className="txt-compact-xsmall text-ui-fg-muted">
                        {offer.sentByName ?? "Staff"}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      {offer.decidedAt ? (
                        <>
                          <div>{date(offer.decidedAt)}</div>
                          <div className="txt-compact-xsmall text-ui-fg-muted">
                            {offer.decidedByName ?? "-"}
                          </div>
                        </>
                      ) : (
                        "-"
                      )}
                    </Table.Cell>
                    <Table.Cell className="w-[1%] whitespace-nowrap text-right">
                      {offer.status === "pending" ? (
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => setWithdrawing(offer)}
                        >
                          Withdraw
                        </Button>
                      ) : null}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Container>

      <Prompt open={withdrawing !== null} onOpenChange={(open) => !open && setWithdrawing(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Withdraw this offer?</Prompt.Title>
            <Prompt.Description>
              {withdrawing
                ? `${withdrawing.workspaceName} can no longer accept ${withdrawing.plan.name}. Nothing on their account changes.`
                : ""}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep it</Prompt.Cancel>
            <Button
              size="small"
              variant="danger"
              isLoading={withdraw.isPending}
              onClick={() => withdrawing && withdraw.mutate(withdrawing)}
            >
              Withdraw
            </Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>

      <Prompt open={renewing} onOpenChange={setRenewing}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Renew monthly plans now?</Prompt.Title>
            <Prompt.Description>
              The API does this every hour. Running it now charges any monthly plan whose month has
              ended, exactly as the hourly run would, and moves canceled plans off at the end of
              their month. A month already charged is never charged again.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Not now</Prompt.Cancel>
            <Button size="small" isLoading={renew.isPending} onClick={() => renew.mutate()}>
              Renew now
            </Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  );
}
